'use client';

import { useState, useCallback } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent, IbanValidationResult } from '@/lib/tools/types';
import { CheckCircle, Copy, Check, RotateCcw, ArrowRight, XCircle, AlertCircle, Globe, Shield } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';

interface IbanValidatorProps {
  locale: Locale;
  content: ToolContent;
}

// Bank BIC codes mapping for common banks
const BANK_CODES: Record<string, { bic: string; name: string }> = {
  // Netherlands (4-char bank code from IBAN positions 4-8)
  'ABNA': { bic: 'ABNANL2A', name: 'ABN AMRO' },
  'INGB': { bic: 'INGBNL2A', name: 'ING Bank' },
  'RABO': { bic: 'RABONL2U', name: 'Rabobank' },
  'SNSB': { bic: 'SNSBNL2A', name: 'SNS Bank' },
  'ASNB': { bic: 'ASNBNL21', name: 'ASN Bank' },
  'TRIO': { bic: 'TRIONL2U', name: 'Triodos Bank' },
  'BUNQ': { bic: 'BUNQNL2A', name: 'bunq' },
  'KNAB': { bic: 'KNABNL2H', name: 'Knab' },
  'REVO': { bic: 'REVOLT21', name: 'Revolut' },
  'RBRB': { bic: 'RBRBNL21', name: 'RegioBank' },
  'N26B': { bic: 'N26BNL2A', name: 'N26' },

  // Belgium (3-digit bank number from IBAN positions 4-7)
  '000': { bic: 'GEBABEBB', name: 'BNP Paribas Fortis' },
  '001': { bic: 'GEBABEBB', name: 'BNP Paribas Fortis' },
  '539': { bic: 'GEBABEBB', name: 'BNP Paribas Fortis' },
  '400': { bic: 'KREDBEBB', name: 'KBC Bank' },
  '401': { bic: 'KREDBEBB', name: 'KBC Bank' },
  '063': { bic: 'GKCCBEBB', name: 'Belfius Bank' },
  '068': { bic: 'GKCCBEBB', name: 'Belfius Bank' },
  '310': { bic: 'BBRUBEBB', name: 'ING Belgium' },
  '311': { bic: 'BBRUBEBB', name: 'ING Belgium' },
  '979': { bic: 'ARSPBE22', name: 'Argenta' },
  '780': { bic: 'CTBKBEBX', name: 'Beobank' },
  '730': { bic: 'CREGBEBB', name: 'CBC Banque' },
  '731': { bic: 'CREGBEBB', name: 'CBC Banque' },

  // Germany (8-digit Bankleitzahl from IBAN positions 4-12)
  '37040044': { bic: 'COBADEFF', name: 'Commerzbank' },
  '10070000': { bic: 'DEUTDEDB', name: 'Deutsche Bank Berlin' },
  '50070010': { bic: 'DEUTDEFF', name: 'Deutsche Bank Frankfurt' },
  '10010010': { bic: 'PBNKDEFF', name: 'Postbank' },
  '10019610': { bic: 'PBNKDEFF', name: 'Postbank' },
  '10077777': { bic: 'NTSBDEB1', name: 'N26 Bank' },
  '12030000': { bic: 'BYLADEM1001', name: 'DKB (Deutsche Kreditbank)' },
  '50010517': { bic: 'INGDDEFF', name: 'ING-DiBa' },
  '20041111': { bic: 'COBADEHDXXX', name: 'comdirect' },
  '50060400': { bic: 'GENODEFF', name: 'DZ Bank' },

  // France (5-digit bank code from IBAN positions 4-9)
  '30004': { bic: 'BNPAFRPP', name: 'BNP Paribas' },
  '30003': { bic: 'SOGEFRPP', name: 'Societe Generale' },
  '30002': { bic: 'CRLYFRPP', name: 'Credit Lyonnais (LCL)' },
  '30006': { bic: 'AGRIFRPP', name: 'Credit Agricole' },
  '10278': { bic: 'CMCIFRPP', name: 'Credit Mutuel' },
  '20041': { bic: 'PSSTFRPP', name: 'La Banque Postale' },
  '10096': { bic: 'CMCIFRPP', name: 'CIC' },
  '11315': { bic: 'CEPAFRPP', name: "Caisse d'Epargne" },

  // Austria
  '12000': { bic: 'BKAUATWW', name: 'UniCredit Bank Austria' },
  '20111': { bic: 'GIBAATWW', name: 'Erste Bank' },
  '36000': { bic: 'RZTIAT22', name: 'Raiffeisen Bank' },

  // UK
  // UK uses sort code (6 digits) at IBAN positions 4-10
};

// Country-specific IBAN lengths
const IBAN_LENGTHS: Record<string, number> = {
  NL: 18,
  BE: 16,
  DE: 22,
  FR: 27,
  AT: 20,
  CH: 21,
  ES: 24,
  IT: 27,
  GB: 22,
  LU: 20,
  PL: 28,
  PT: 25,
  IE: 22,
  SE: 24,
  DK: 18,
  FI: 18,
  NO: 15,
  CZ: 24,
  HU: 28,
  RO: 24,
  BG: 22,
  HR: 21,
  SK: 24,
  SI: 19,
  LT: 20,
  LV: 21,
  EE: 20,
  CY: 28,
  MT: 31,
  GR: 27,
  TR: 26,
  IS: 26,
  LI: 21,
  MC: 27,
  SM: 27,
  AD: 24,
};

// Country information with locale-specific names
const COUNTRY_INFO: Record<string, { name: Record<string, string>; flag: string; sepa: boolean; ibanExample: string }> = {
  NL: {
    name: { nl: 'Nederland', en: 'Netherlands', de: 'Niederlande', fr: 'Pays-Bas' },
    flag: '\u{1F1F3}\u{1F1F1}', sepa: true, ibanExample: 'NL91 ABNA 0417 1643 00',
  },
  BE: {
    name: { nl: 'Belgi\u00eb', en: 'Belgium', de: 'Belgien', fr: 'Belgique' },
    flag: '\u{1F1E7}\u{1F1EA}', sepa: true, ibanExample: 'BE68 5390 0754 7034',
  },
  DE: {
    name: { nl: 'Duitsland', en: 'Germany', de: 'Deutschland', fr: 'Allemagne' },
    flag: '\u{1F1E9}\u{1F1EA}', sepa: true, ibanExample: 'DE89 3704 0044 0532 0130 00',
  },
  FR: {
    name: { nl: 'Frankrijk', en: 'France', de: 'Frankreich', fr: 'France' },
    flag: '\u{1F1EB}\u{1F1F7}', sepa: true, ibanExample: 'FR76 3000 6000 0112 3456 7890 189',
  },
  AT: {
    name: { nl: 'Oostenrijk', en: 'Austria', de: '\u00d6sterreich', fr: 'Autriche' },
    flag: '\u{1F1E6}\u{1F1F9}', sepa: true, ibanExample: 'AT61 1904 3002 3457 3201',
  },
  CH: {
    name: { nl: 'Zwitserland', en: 'Switzerland', de: 'Schweiz', fr: 'Suisse' },
    flag: '\u{1F1E8}\u{1F1ED}', sepa: true, ibanExample: 'CH93 0076 2011 6238 5295 7',
  },
  ES: {
    name: { nl: 'Spanje', en: 'Spain', de: 'Spanien', fr: 'Espagne' },
    flag: '\u{1F1EA}\u{1F1F8}', sepa: true, ibanExample: 'ES91 2100 0418 4502 0005 1332',
  },
  IT: {
    name: { nl: 'Itali\u00eb', en: 'Italy', de: 'Italien', fr: 'Italie' },
    flag: '\u{1F1EE}\u{1F1F9}', sepa: true, ibanExample: 'IT60 X054 2811 1010 0000 0123 456',
  },
  GB: {
    name: { nl: 'Verenigd Koninkrijk', en: 'United Kingdom', de: 'Vereinigtes K\u00f6nigreich', fr: 'Royaume-Uni' },
    flag: '\u{1F1EC}\u{1F1E7}', sepa: false, ibanExample: 'GB29 NWBK 6016 1331 9268 19',
  },
  LU: {
    name: { nl: 'Luxemburg', en: 'Luxembourg', de: 'Luxemburg', fr: 'Luxembourg' },
    flag: '\u{1F1F1}\u{1F1FA}', sepa: true, ibanExample: 'LU28 0019 4006 4475 0000',
  },
  PL: {
    name: { nl: 'Polen', en: 'Poland', de: 'Polen', fr: 'Pologne' },
    flag: '\u{1F1F5}\u{1F1F1}', sepa: true, ibanExample: 'PL61 1090 1014 0000 0712 1981 2874',
  },
  PT: {
    name: { nl: 'Portugal', en: 'Portugal', de: 'Portugal', fr: 'Portugal' },
    flag: '\u{1F1F5}\u{1F1F9}', sepa: true, ibanExample: 'PT50 0002 0123 1234 5678 9015 4',
  },
  IE: {
    name: { nl: 'Ierland', en: 'Ireland', de: 'Irland', fr: 'Irlande' },
    flag: '\u{1F1EE}\u{1F1EA}', sepa: true, ibanExample: 'IE29 AIBK 9311 5212 3456 78',
  },
  SE: {
    name: { nl: 'Zweden', en: 'Sweden', de: 'Schweden', fr: 'Su\u00e8de' },
    flag: '\u{1F1F8}\u{1F1EA}', sepa: true, ibanExample: 'SE45 5000 0000 0583 9825 7466',
  },
  DK: {
    name: { nl: 'Denemarken', en: 'Denmark', de: 'D\u00e4nemark', fr: 'Danemark' },
    flag: '\u{1F1E9}\u{1F1F0}', sepa: true, ibanExample: 'DK50 0040 0440 1162 43',
  },
  FI: {
    name: { nl: 'Finland', en: 'Finland', de: 'Finnland', fr: 'Finlande' },
    flag: '\u{1F1EB}\u{1F1EE}', sepa: true, ibanExample: 'FI21 1234 5600 0007 85',
  },
  NO: {
    name: { nl: 'Noorwegen', en: 'Norway', de: 'Norwegen', fr: 'Norv\u00e8ge' },
    flag: '\u{1F1F3}\u{1F1F4}', sepa: true, ibanExample: 'NO93 8601 1117 947',
  },
  CZ: {
    name: { nl: 'Tsjechi\u00eb', en: 'Czech Republic', de: 'Tschechien', fr: 'R\u00e9publique tch\u00e8que' },
    flag: '\u{1F1E8}\u{1F1FF}', sepa: true, ibanExample: 'CZ65 0800 0000 1920 0014 5399',
  },
  HU: {
    name: { nl: 'Hongarije', en: 'Hungary', de: 'Ungarn', fr: 'Hongrie' },
    flag: '\u{1F1ED}\u{1F1FA}', sepa: true, ibanExample: 'HU42 1177 3016 1111 1018 0000 0000',
  },
  RO: {
    name: { nl: 'Roemeni\u00eb', en: 'Romania', de: 'Rum\u00e4nien', fr: 'Roumanie' },
    flag: '\u{1F1F7}\u{1F1F4}', sepa: true, ibanExample: 'RO49 AAAA 1B31 0075 9384 0000',
  },
  BG: {
    name: { nl: 'Bulgarije', en: 'Bulgaria', de: 'Bulgarien', fr: 'Bulgarie' },
    flag: '\u{1F1E7}\u{1F1EC}', sepa: true, ibanExample: 'BG80 BNBG 9661 1020 3456 78',
  },
  HR: {
    name: { nl: 'Kroati\u00eb', en: 'Croatia', de: 'Kroatien', fr: 'Croatie' },
    flag: '\u{1F1ED}\u{1F1F7}', sepa: true, ibanExample: 'HR12 1001 0051 8630 0016 0',
  },
  SK: {
    name: { nl: 'Slowakije', en: 'Slovakia', de: 'Slowakei', fr: 'Slovaquie' },
    flag: '\u{1F1F8}\u{1F1F0}', sepa: true, ibanExample: 'SK31 1200 0000 1987 4263 7541',
  },
  SI: {
    name: { nl: 'Sloveni\u00eb', en: 'Slovenia', de: 'Slowenien', fr: 'Slov\u00e9nie' },
    flag: '\u{1F1F8}\u{1F1EE}', sepa: true, ibanExample: 'SI56 2633 0001 2039 086',
  },
  LT: {
    name: { nl: 'Litouwen', en: 'Lithuania', de: 'Litauen', fr: 'Lituanie' },
    flag: '\u{1F1F1}\u{1F1F9}', sepa: true, ibanExample: 'LT12 1000 0111 0100 1000',
  },
  LV: {
    name: { nl: 'Letland', en: 'Latvia', de: 'Lettland', fr: 'Lettonie' },
    flag: '\u{1F1F1}\u{1F1FB}', sepa: true, ibanExample: 'LV80 BANK 0000 4351 9500 1',
  },
  EE: {
    name: { nl: 'Estland', en: 'Estonia', de: 'Estland', fr: 'Estonie' },
    flag: '\u{1F1EA}\u{1F1EA}', sepa: true, ibanExample: 'EE38 2200 2210 2014 5685',
  },
  CY: {
    name: { nl: 'Cyprus', en: 'Cyprus', de: 'Zypern', fr: 'Chypre' },
    flag: '\u{1F1E8}\u{1F1FE}', sepa: true, ibanExample: 'CY17 0020 0128 0000 0012 0052 7600',
  },
  MT: {
    name: { nl: 'Malta', en: 'Malta', de: 'Malta', fr: 'Malte' },
    flag: '\u{1F1F2}\u{1F1F9}', sepa: true, ibanExample: 'MT84 MALT 0110 0001 2345 MTLC AST0 01S',
  },
  GR: {
    name: { nl: 'Griekenland', en: 'Greece', de: 'Griechenland', fr: 'Gr\u00e8ce' },
    flag: '\u{1F1EC}\u{1F1F7}', sepa: true, ibanExample: 'GR16 0110 1250 0000 0001 2300 695',
  },
  TR: {
    name: { nl: 'Turkije', en: 'Turkey', de: 'T\u00fcrkei', fr: 'Turquie' },
    flag: '\u{1F1F9}\u{1F1F7}', sepa: false, ibanExample: 'TR33 0006 1005 1978 6457 8413 26',
  },
  IS: {
    name: { nl: 'IJsland', en: 'Iceland', de: 'Island', fr: 'Islande' },
    flag: '\u{1F1EE}\u{1F1F8}', sepa: true, ibanExample: 'IS14 0159 2600 7654 5510 7303 39',
  },
  LI: {
    name: { nl: 'Liechtenstein', en: 'Liechtenstein', de: 'Liechtenstein', fr: 'Liechtenstein' },
    flag: '\u{1F1F1}\u{1F1EE}', sepa: true, ibanExample: 'LI21 0881 0000 2324 013A A',
  },
  MC: {
    name: { nl: 'Monaco', en: 'Monaco', de: 'Monaco', fr: 'Monaco' },
    flag: '\u{1F1F2}\u{1F1E8}', sepa: true, ibanExample: 'MC58 1122 2000 0101 2345 6789 030',
  },
  SM: {
    name: { nl: 'San Marino', en: 'San Marino', de: 'San Marino', fr: 'Saint-Marin' },
    flag: '\u{1F1F8}\u{1F1F2}', sepa: true, ibanExample: 'SM86 U032 2509 8000 0000 0270 100',
  },
  AD: {
    name: { nl: 'Andorra', en: 'Andorra', de: 'Andorra', fr: 'Andorre' },
    flag: '\u{1F1E6}\u{1F1E9}', sepa: true, ibanExample: 'AD12 0001 2030 2003 5910 0100',
  },
};

// IBAN structure definitions per country: [bankCodeStart, bankCodeEnd, accountStart]
const IBAN_STRUCTURE: Record<string, { bankStart: number; bankEnd: number; accountStart: number }> = {
  NL: { bankStart: 4, bankEnd: 8, accountStart: 8 },
  BE: { bankStart: 4, bankEnd: 7, accountStart: 7 },
  DE: { bankStart: 4, bankEnd: 12, accountStart: 12 },
  FR: { bankStart: 4, bankEnd: 9, accountStart: 9 },
  AT: { bankStart: 4, bankEnd: 9, accountStart: 9 },
  CH: { bankStart: 4, bankEnd: 9, accountStart: 9 },
  ES: { bankStart: 4, bankEnd: 8, accountStart: 8 },
  IT: { bankStart: 5, bankEnd: 10, accountStart: 10 },
  GB: { bankStart: 4, bankEnd: 10, accountStart: 10 },
  LU: { bankStart: 4, bankEnd: 7, accountStart: 7 },
};

function extractBankCode(countryCode: string, cleanIban: string): string {
  const structure = IBAN_STRUCTURE[countryCode];
  if (structure) {
    return cleanIban.substring(structure.bankStart, structure.bankEnd);
  }
  // Default: try positions 4-8 for unknown countries
  return cleanIban.substring(4, 8);
}

function extractAccountNumber(countryCode: string, cleanIban: string): string {
  const structure = IBAN_STRUCTURE[countryCode];
  if (structure) {
    return cleanIban.substring(structure.accountStart);
  }
  return cleanIban.substring(8);
}

function lookupBank(countryCode: string, bankCode: string): { bic: string; name: string } | undefined {
  // Direct lookup
  const direct = BANK_CODES[bankCode];
  if (direct) return direct;

  // For Belgium, IBAN has 3-digit bank code, try first 3 chars as key
  if (countryCode === 'BE') {
    const be3 = BANK_CODES[bankCode.substring(0, 3)];
    if (be3) return be3;
    const be4 = BANK_CODES[bankCode.substring(0, 4)];
    if (be4) return be4;
  }

  // For Germany, try prefix matching for Sparkasse/Volksbank patterns
  if (countryCode === 'DE') {
    // Sparkasse BLZ start with certain prefixes by region
    if (bankCode.startsWith('100') || bankCode.startsWith('200') || bankCode.startsWith('300') ||
        bankCode.startsWith('400') || bankCode.startsWith('500') || bankCode.startsWith('600') ||
        bankCode.startsWith('700') || bankCode.startsWith('800')) {
      // Check if it's a known BLZ before falling back
      const knownBlz = BANK_CODES[bankCode];
      if (knownBlz) return knownBlz;
    }
  }

  return undefined;
}

function validateIban(iban: string): IbanValidationResult {
  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Check minimum length
  if (cleanIban.length < 5) {
    return { valid: false, errorMessage: 'invalidFormat' };
  }

  // Extract country code
  const countryCode = cleanIban.substring(0, 2);

  // Check if country is supported
  if (!IBAN_LENGTHS[countryCode]) {
    return { valid: false, errorMessage: 'unsupportedCountry', countryCode };
  }

  // Check length for country
  if (cleanIban.length !== IBAN_LENGTHS[countryCode]) {
    return { valid: false, errorMessage: 'invalidFormat', countryCode };
  }

  // Rearrange: move first 4 characters to end
  const rearranged = cleanIban.substring(4) + cleanIban.substring(0, 4);

  // Convert letters to numbers (A=10, B=11, etc.)
  let numericString = '';
  for (const char of rearranged) {
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Perform modulo 97 check
  let remainder = 0;
  for (let i = 0; i < numericString.length; i++) {
    remainder = parseInt(remainder.toString() + numericString[i], 10) % 97;
  }

  if (remainder !== 1) {
    return { valid: false, errorMessage: 'invalidChecksum', countryCode };
  }

  // Extract parts
  const checkDigits = cleanIban.substring(2, 4);
  const bankCode = extractBankCode(countryCode, cleanIban);
  const accountNumber = extractAccountNumber(countryCode, cleanIban);

  // Look up bank info
  const bankInfo = lookupBank(countryCode, bankCode);

  // Format IBAN with spaces every 4 characters
  const formattedIban = cleanIban.match(/.{1,4}/g)?.join(' ') ?? cleanIban;

  // SEPA status
  const countryInfo = COUNTRY_INFO[countryCode];
  const sepa = countryInfo?.sepa ?? false;

  return {
    valid: true,
    countryCode,
    bankCode,
    bankName: bankInfo?.name,
    bic: bankInfo?.bic,
    formattedIban,
    checkDigits,
    accountNumber,
    sepa,
  };
}

// Example IBAN ordering per locale
const EXAMPLE_ORDER: Record<string, string[]> = {
  nl: ['NL', 'BE', 'DE', 'FR', 'AT', 'GB', 'ES', 'IT', 'LU', 'CH', 'PL', 'PT', 'IE', 'SE'],
  en: ['GB', 'NL', 'DE', 'FR', 'BE', 'ES', 'IT', 'AT', 'CH', 'IE', 'SE', 'PL', 'PT', 'LU'],
  de: ['DE', 'AT', 'CH', 'NL', 'BE', 'FR', 'LU', 'IT', 'ES', 'GB', 'PL', 'CZ', 'SE', 'PT'],
  fr: ['FR', 'BE', 'LU', 'NL', 'MC', 'CH', 'DE', 'ES', 'IT', 'PT', 'GB', 'AT', 'IE', 'SE'],
};

export function IbanValidator({ locale, content }: IbanValidatorProps) {
  const [iban, setIban] = useState<string>('');
  const [result, setResult] = useState<IbanValidationResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const labels = content.labels;
  const errors = content.errors ?? {};

  const handleValidate = useCallback(() => {
    if (!iban.trim()) {
      setResult({ valid: false, errorMessage: 'ibanRequired' });
      return;
    }
    setResult(validateIban(iban));
  }, [iban]);

  const handleInputChange = (value: string) => {
    setIban(value);
    // Auto-validate on change if there's already a result
    if (result && value.replace(/\s/g, '').length >= 5) {
      setResult(validateIban(value));
    }
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const reset = () => {
    setIban('');
    setResult(null);
  };

  const getErrorMessage = (errorKey?: string) => {
    if (!errorKey) return '';
    return errors[errorKey] ?? errorKey;
  };

  const getCountryName = (code: string): string => {
    const info = COUNTRY_INFO[code];
    if (!info) return code;
    return info.name[locale] ?? info.name['en'] ?? code;
  };

  const getCountryFlag = (code: string): string => {
    return COUNTRY_INFO[code]?.flag ?? '';
  };

  const exampleCountries = EXAMPLE_ORDER[locale] ?? EXAMPLE_ORDER['en'];

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100">
          <CheckCircle className="h-7 w-7 text-purple-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">
          {content.description}
        </p>
      </div>

      {/* Validator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* IBAN Input */}
        <div className="mb-6">
          <label htmlFor="iban" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.iban}
          </label>
          <input
            type="text"
            id="iban"
            value={iban}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
            placeholder={labels.enterIban}
            className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-lg font-mono uppercase tracking-wider focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Validate Button */}
        <button
          type="button"
          onClick={handleValidate}
          className="w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700"
        >
          {labels.validate}
        </button>

        {/* Result */}
        {result && (
          <div className={`mt-6 rounded-xl p-4 ${result.valid ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3">
              {result.valid ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <span className={`font-semibold ${result.valid ? 'text-green-800' : 'text-red-800'}`}>
                {result.valid ? labels.valid : labels.invalid}
              </span>
            </div>

            {!result.valid && result.errorMessage && (
              <p className="mt-2 flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4" />
                {getErrorMessage(result.errorMessage)}
              </p>
            )}

            {result.valid && result.countryCode && (
              <div className="mt-4 space-y-3">
                {/* Country + SEPA Row */}
                <div className="flex items-center gap-3 rounded-lg bg-white p-3">
                  <div className="flex-1">
                    <span className="block text-xs text-slate-500">{labels.countryName}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {getCountryFlag(result.countryCode)} {getCountryName(result.countryCode)}
                    </span>
                  </div>
                  {result.sepa !== undefined && (
                    <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${result.sepa ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {result.sepa ? (
                        <>
                          <Shield className="h-3.5 w-3.5" />
                          {labels.sepaYes}
                        </>
                      ) : (
                        <>
                          <Globe className="h-3.5 w-3.5" />
                          {labels.sepaNo}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Formatted IBAN */}
                <div className="flex items-center justify-between rounded-lg bg-white p-3">
                  <div>
                    <span className="block text-xs text-slate-500">{labels.formattedIban}</span>
                    <span className="font-mono text-sm font-medium text-slate-900">{result.formattedIban}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.formattedIban ?? '', 'iban')}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    title={labels.copyIban}
                  >
                    {copied === 'iban' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>

                {/* Bank Code */}
                {result.bankCode && (
                  <div className="flex items-center justify-between rounded-lg bg-white p-3">
                    <div>
                      <span className="block text-xs text-slate-500">{labels.bankCode}</span>
                      <span className="text-sm font-medium text-slate-900">{result.bankCode}</span>
                    </div>
                  </div>
                )}

                {/* Bank Name */}
                {result.bankName && (
                  <div className="flex items-center justify-between rounded-lg bg-white p-3">
                    <div>
                      <span className="block text-xs text-slate-500">{labels.bankName}</span>
                      <span className="text-sm font-medium text-slate-900">{result.bankName}</span>
                    </div>
                  </div>
                )}

                {/* BIC Code */}
                {result.bic && (
                  <div className="flex items-center justify-between rounded-lg bg-white p-3">
                    <div>
                      <span className="block text-xs text-slate-500">{labels.bic}</span>
                      <span className="font-mono text-sm font-medium text-slate-900">{result.bic}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(result.bic ?? '', 'bic')}
                      className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      title={labels.copyBic}
                    >
                      {copied === 'bic' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                )}

                {/* IBAN Structure Breakdown */}
                <div className="rounded-lg bg-white p-3">
                  <span className="mb-2 block text-xs text-slate-500">{labels.ibanStructure}</span>
                  <div className="overflow-x-auto">
                    <div className="flex gap-1 font-mono text-sm">
                      <div className="flex flex-col items-center">
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700">{result.countryCode}</span>
                        <span className="mt-1 text-[10px] text-blue-600">{labels.countryCode}</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">{result.checkDigits}</span>
                        <span className="mt-1 text-[10px] text-amber-600">{labels.checkDigits}</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="rounded bg-purple-100 px-1.5 py-0.5 font-semibold text-purple-700">{result.bankCode}</span>
                        <span className="mt-1 max-w-[80px] truncate text-[10px] text-purple-600">{labels.bankCode}{result.bankName ? ` (${result.bankName})` : ''}</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="rounded bg-green-100 px-1.5 py-0.5 font-semibold text-green-700">{result.accountNumber}</span>
                        <span className="mt-1 text-[10px] text-green-600">{labels.accountNumber}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clear Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.clear}
        </button>
      </div>

      {/* Example IBANs Section */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">
          {labels.exampleIbans}
        </h2>
        <div className="space-y-2">
          {exampleCountries.map((code) => {
            const info = COUNTRY_INFO[code];
            if (!info) return null;
            return (
              <div key={code} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">{info.flag}</span>
                  <span className="text-sm font-medium text-slate-700">
                    {info.name[locale] ?? info.name['en']}
                  </span>
                </div>
                <span className="font-mono text-xs text-slate-500">{info.ibanExample}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment \u00e7a marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}
    </div>
  );
}
